import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bed, Bath, Edit2, Package, Droplet } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { fetchHospedajeById } from '@/services/hospedajes.service';
import { updateHospedajeCamas, updateHospedajeBanos } from '@/pages/parametrizacion/hospedajes/Services/hospedajes.services';
import GestionarCamasModal from '@/pages/parametrizacion/hospedajes/components/GestionarCamasModal';
import GestionarBanosModal from '@/pages/parametrizacion/hospedajes/components/GestionarBanosModal';

const InventarioHabitacion = () => {
  const { hospedajeId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [hospedaje, setHospedaje] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCamasModalOpen, setIsCamasModalOpen] = useState(false);
  const [isBanosModalOpen, setIsBanosModalOpen] = useState(false);

  useEffect(() => {
    loadHospedaje();
  }, [hospedajeId]);

  const loadHospedaje = async () => {
    setIsLoading(true);
    try {
      const response = await fetchHospedajeById(hospedajeId);
      if (response && response.data) {
        setHospedaje(response.data);
      }
    } catch (error) {
      console.error('Error al cargar hospedaje:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la información del activo',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCamas = async (hospedajeId, camas) => {
    try {
      await updateHospedajeCamas(hospedajeId, camas);
      await loadHospedaje();
      toast({
        title: 'Éxito',
        description: 'Camas actualizadas correctamente.',
      });
    } catch (error) {
      console.error('Error al actualizar camas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron actualizar las camas.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleSaveBanos = async (hospedajeId, banos) => {
    try {
      await updateHospedajeBanos(hospedajeId, banos);
      await loadHospedaje();
      toast({
        title: 'Éxito',
        description: 'Baños actualizados correctamente.',
      });
    } catch (error) {
      console.error('Error al actualizar baños:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron actualizar los baños.',
        variant: 'destructive',
      });
      throw error;
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Bed className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando inventario...</p>
        </div>
      </div>
    );
  }

  if (!hospedaje) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Bed className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Activo no encontrado
          </h3>
          <p className="text-gray-500 mb-4">
            No se pudo encontrar la información del activo solicitado
          </p>
          <Button onClick={() => navigate(-1)}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Inventario de Habitación</h1>
            <p className="text-muted-foreground">{hospedaje.nombrePropiedad}</p>
          </div>
        </div>
      </div>

      {/* Información del Activo */}
      <Card>
        <CardHeader>
          <CardTitle>Información del Activo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Portafolio</p>
              <p className="font-semibold">{hospedaje.edificio?.nombre || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Coordinador</p>
              <p className="font-semibold">{hospedaje.coordinador?.nombre || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>



      {/* Lista de Camas Parametrizadas */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Camas</CardTitle>
              <CardDescription>
                Tipos de camas configurados para este activo
              </CardDescription>
            </div>
            <Button
              onClick={() => setIsCamasModalOpen(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Gestionar Camas
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!hospedaje.camas || hospedaje.camas.length === 0 ? (
            <div className="text-center py-12">
              <Bed className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay camas configuradas
              </h3>
              <p className="text-gray-500 mb-4">
                Agrega tipos de camas para este activo haciendo clic en "Gestionar Camas"
              </p>
              <Button
                onClick={() => setIsCamasModalOpen(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Bed className="h-4 w-4 mr-2" />
                Configurar Camas
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {hospedaje.camas.map((cama, index) => (
                <Card key={index} className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <Bed className="h-8 w-8 text-purple-600" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{cama.nombre}</h4>
                          <p className="text-sm text-muted-foreground">
                            Tipo de cama parametrizada
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-purple-600">
                          {cama.cantidad}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {cama.cantidad === 1 ? 'unidad' : 'unidades'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de Baños Parametrizados */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Baños</CardTitle>
              <CardDescription>
                Tipos de baños configurados para este activo
              </CardDescription>
            </div>
            <Button
              onClick={() => setIsBanosModalOpen(true)}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Gestionar Baños
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!hospedaje.banos || hospedaje.banos.length === 0 ? (
            <div className="text-center py-12">
              <Bath className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay baños configurados
              </h3>
              <p className="text-gray-500 mb-4">
                Agrega tipos de baños para este activo haciendo clic en "Gestionar Baños"
              </p>
              <Button
                onClick={() => setIsBanosModalOpen(true)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Bath className="h-4 w-4 mr-2" />
                Configurar Baños
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {hospedaje.banos.map((bano, index) => (
                <Card key={index} className="border-l-4 border-l-teal-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <Bath className="h-8 w-8 text-teal-600" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{bano.nombre}</h4>
                          <p className="text-sm text-muted-foreground">
                            Tipo de baño parametrizado
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-teal-600">
                          {bano.cantidad}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {bano.cantidad === 1 ? 'unidad' : 'unidades'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inventario General */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Inventario General
              </CardTitle>
              <CardDescription>
                Mobiliario y elementos de la habitación
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-gray-500">
              Próximamente
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Funcionalidad en desarrollo
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Aquí podrás gestionar el inventario general de mobiliario y elementos de la habitación
            </p>
            <div className="text-sm text-gray-400">
              • Muebles y decoración<br />
              • Electrodomésticos<br />
              • Elementos de baño<br />
              • Utensilios de cocina
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insumos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Droplet className="h-5 w-5 text-teal-600" />
                Insumos
              </CardTitle>
              <CardDescription>
                Productos de limpieza y amenities
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-gray-500">
              Próximamente
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Droplet className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Funcionalidad en desarrollo
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Aquí podrás gestionar los insumos y amenities de la habitación
            </p>
            <div className="text-sm text-gray-400">
              • Productos de limpieza<br />
              • Amenities (shampoo, jabón, etc.)<br />
              • Papel higiénico y toallas de papel<br />
              • Otros consumibles
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Gestión de Camas */}
      <GestionarCamasModal
        isOpen={isCamasModalOpen}
        onClose={() => setIsCamasModalOpen(false)}
        hospedaje={hospedaje}
        onSave={handleSaveCamas}
      />

      {/* Modal de Gestión de Baños */}
      <GestionarBanosModal
        isOpen={isBanosModalOpen}
        onClose={() => setIsBanosModalOpen(false)}
        hospedaje={hospedaje}
        onSave={handleSaveBanos}
      />
    </motion.div>
  );
};

export default InventarioHabitacion;
